import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FacadeService } from 'src/app/core/services/facade.service';
import { LogService } from 'src/app/core/services/log/log.service';

import { User } from 'src/app/shared/models/security/user';
import { Employee } from 'src/app/shared/models/employee/employee';
import { saveAs } from 'file-saver';
import { PARAM_LABEL_INTERVENANT } from 'src/app/core/services/constant/constants.service';
import { Specialite } from 'src/app/shared/models/specialite';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { EmployeeModalAjoutComponent } from '../modal/employee-modal-ajout/employee-modal-ajout.component';
import { ImportResponse } from 'src/app/shared/models/api-response/import-response';
import { ErrorResponse } from 'src/app/shared/models/api-response/error-response';
import { EmployeeTypeString } from 'src/app/shared/models/employee/employee-type';
import { CareSkill } from 'src/app/shared/models/employee/care-skill';
import { CenterDepartment } from 'src/app/shared/models/center-department';
import { ConvertObjectToCalendarEvent } from 'src/app/shared/utils/convert-object-to-calendar-event';

export type SortOrder = 'asc' | 'desc';

@Component({
	selector: 'app-list-employee',
	templateUrl: './list-employee.component.html',
	styleUrls: ['./list-employee.component.scss']
})
export class ListEmployeeComponent implements OnInit {
	LABEL_INTERVENANT: string;
	LABEL_INTERVENANT_CAPITALIZED;
	LABEL_INTERVENANT_PLURIALIZED: string;

	employees: Employee[] = [];
	currentPage = 0;

	employeeCsvFile: File = null;

	dismissible: true;
	alerts: any[] = [];
	competences: CareSkill[];
	user: User = null;

	isAddable = false;
	isImportable = false;
	isExportable = false;

	isSESSAD = false;
	isSAAD = false;
	isSSIAD = false;
	isSAE = false;

	isAreaActive = false;

	sortType = 'firstName';
	sortOrder: SortOrder = 'asc';

	selectedCompetences: string[] = [];
	searchedText: string;

	listSpecialite: Specialite[];
	listSelectedSpecialite: Specialite[] = [];

	centerDepartments: CenterDepartment[] = [];
	listSelectedDepartment: CenterDepartment[] = [];

	ajoutIntervenantModalref: BsModalRef;

	itemsPerPage = 10;

	isMobile = false;
	screenWidth: number;

	isListingLoading: boolean = true;

	employeeTypeString = EmployeeTypeString;

	colorsPastel;

	constructor(
		private router: Router,
		private logger: LogService,
		private facade: FacadeService,
		private modalService: BsModalService,
		private convertObjectCalendar: ConvertObjectToCalendarEvent
	) {}

	@HostListener('window:resize', ['$event'])
	onWindowResize() {
		this.screenWidth = window.innerWidth;
		if (this.screenWidth < 1200) {
			this.isMobile = true;
		} else {
			this.isMobile = false;
		}
	}

	ngOnInit(): void {
		this.colorsPastel = this.convertObjectCalendar.colorsPastel;
		this.screenWidth = window.innerWidth;
		this.isMobile =
			this.facade.mobileService.isMobile() || this.screenWidth < 1200;

		this.LABEL_INTERVENANT_CAPITALIZED =
			this.facade.dataStorageService.getLabelCapitalized(
				PARAM_LABEL_INTERVENANT
			);
		this.LABEL_INTERVENANT_PLURIALIZED =
			this.facade.dataStorageService.getLabelPluralized(
				PARAM_LABEL_INTERVENANT
			);

		this.user = this.facade.dataStorageService.getUser();
		if (this.facade.dataStorageService.isPlanificatrice()) {
			this.isAddable = true;
			this.isImportable = true;
			this.isExportable = true;
		}

		this.isSESSAD = this.facade.dataStorageService.isSESSAD();
		this.isSAAD = this.facade.dataStorageService.isSAAD();
		this.isSSIAD = this.facade.dataStorageService.isSSIAD();
		this.isSAE = this.facade.dataStorageService.isSAE();

		this.isAreaActive = this.facade.dataStorageService.isAreaActive();
		this.facade.careSkillService.getAllCareSkills().subscribe({
			next: (careskills: CareSkill[]) => (this.competences = careskills),
			error: (err) => this.logger.error(err)
		});

		this.getEmployees();
	}

	getEmployees(): void {
		this.isListingLoading = true;
		this.facade.employeeService.getAllActiveEmployees().subscribe({
			next: (employees: Employee[]) => {
				this.employees = employees;
				this.listSpecialite = this.getEmployeesSpecialities(this.employees);
				this.currentPage = 1;
				this.itemsPerPage = this.employees.length >= 5 ? 10 : 5;
				this.centerDepartments = this.getEmployeesDepartments(this.employees);
				this.isListingLoading = false;
			},
			error: (err: ErrorResponse) => {
				this.logger.error(err + '');
				this.isListingLoading = false;
			}
		});
	}

	getEmployeeSkillsAsString(employee: Employee): string {
		if (!employee || !employee.careSkills) {
			return 'Sans';
		}

		const skills = employee.careSkills;
		if (skills.length > 0) {
			return skills.map((skill) => skill.name || 'Sans').join(' | ');
		}

		return 'Sans';
	}

	selectEmployee(employee: Employee): void {
		const link = ['/employees', employee.id];
		this.router.navigate(link);
	}

	getEmployeesSpecialities(employees: Employee[]): Specialite[] {
		return employees
			.flatMap((employee) => employee.specialties)
			.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i)
			.sort((a, b) => a.name.localeCompare(b.name));
	}

	getEmployeesDepartments(employees: Employee[]): CenterDepartment[] {
		return employees
			.flatMap((emp) => emp.centerDepartments)
			.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i)
			.sort((a, b) => a.name.localeCompare(b.name));
	}

	importFromOcashe() {
		this.isListingLoading = true; // Si tu as un loader
		this.facade.employeeService.importEmployeesFromOcashe().subscribe({
			next: (result) => {
				this.getEmployees(); // À adapter avec ta méthode de reload
			},
			error: (err) => {
				this.logger.error(err);
			},
			complete: () => {
				// this.isLoading = false;
			}
		});
	}

	hasValidColor(emp: Employee): boolean {
		return (
			emp &&
			emp.colorIndex !== undefined &&
			emp.colorIndex !== null &&
			this.colorsPastel &&
			this.colorsPastel[emp.colorIndex] !== undefined
		);
	}

	importEmployees(event: any): void {
		this.isImportable = false;
		this.logger.log("Import de l'employee");
		this.alerts = [];
		this.employeeCsvFile = event.target.files[0];
		this.facade.employeeService
			.importEmployees(this.employeeCsvFile)
			.subscribe({
				next: (res: ImportResponse) => {
					this.isImportable = true;

					this.logger.log("C'est importé !");

					this.getEmployees();
				},
				error: (err) => {
					this.isImportable = true;
					this.logger.log("C'est PAS importé !", err.message);
					this.facade.toastService.createHttpErrorToast(
						err.error.response,
						err.status
					);
				}
			});
	}

	openModalAjout() {
		const initialState = {};
		this.ajoutIntervenantModalref = this.modalService.show(
			EmployeeModalAjoutComponent,
			{
				class: 'modal-lg',
				initialState,
				backdrop: 'static'
			}
		);

		this.ajoutIntervenantModalref.onHide.subscribe((reason: string | any) => {
			this.getEmployees();
		});
	}

	exportEmployees(): void {
		this.facade.exportService
			.exportEmployees()
			.subscribe((blob) => saveAs(blob, 'employees.csv'));
	}

	reload() {
		const currentUrl = this.router.url;
		this.router.routeReuseStrategy.shouldReuseRoute = () => false;
		this.router.onSameUrlNavigation = 'reload';
		this.router.navigate([currentUrl]);
	}

	sortChange(str: string) {
		if (str === this.sortType) {
			if (this.sortOrder === 'asc') {
				this.sortOrder = 'desc';
			} else {
				this.sortOrder = 'asc';
			}
		} else {
			this.sortType = str;
			this.sortOrder = 'asc';
		}
	}

	/**
	 * Ajoute ou supprime la compétence à la liste des compétences sélectionnées et mets à jour la chaîne de caractères affichée.
	 * @param values Element html
	 * @param competence String representant la compétence choisie
	 */
	changeCompetencesFilter(values: any, competence: string): void {
		// Add
		if (values.currentTarget.checked) {
			const clone = [...this.selectedCompetences];
			clone.push(competence);
			this.selectedCompetences = clone;
		} else {
			//Remove
			this.selectedCompetences = this.selectedCompetences.filter(
				(e) => e !== competence
			);
		}
	}

	/**
	 * Ajoute ou supprime la spécialité à la liste des spécialités sélectionnées
	 * @param e Element html
	 * @param spe String representant la specialite choisie
	 */
	updateSelectedSpecialite(e: any, spe: Specialite) {
		if (e.target.checked) {
			if (!this.listSelectedSpecialite.some((e) => e.id === spe.id)) {
				this.listSelectedSpecialite = [...this.listSelectedSpecialite];
				this.listSelectedSpecialite.push(spe);
			}
		} else {
			if (this.listSelectedSpecialite.some((e) => e.id === spe.id)) {
				this.listSelectedSpecialite = this.listSelectedSpecialite.filter(
					(e) => e.id !== spe.id
				);
			}
		}
	}

	updateSelectedDepartment(e: any, department: CenterDepartment) {
		if (e.target.checked) {
			if (!this.listSelectedDepartment.some((e) => e.id === department.id)) {
				this.listSelectedDepartment = [...this.listSelectedDepartment];
				this.listSelectedDepartment.push(department);
			}
		} else {
			if (this.listSelectedDepartment.some((e) => e.id === department.id)) {
				this.listSelectedDepartment = this.listSelectedDepartment.filter(
					(e) => e.id !== department.id
				);
			}
		}
	}

	openEmployee(id: number): void {
		const url = this.router.serializeUrl(
		  this.router.createUrlTree(['/gestion/employees', id])
		);
	  
		window.open(url, '_blank');
	}
}
